import { ConsoleMatch } from "@/game-fortnite/models/console-matches.mjs";
import { Match } from "@/game-fortnite/models/match-query.mjs";
import { playerMatch } from "@/game-fortnite/models/matchlist.mjs";
import { player } from "@/game-fortnite/models/profile.mjs";
import { fortniteSeasonModel } from "@/game-fortnite/models/season.mjs";
import gql from "@/util/graphql-query.mjs";

export const getProfile = gql`
  query player(
    $accountId: String, 
    $name: String
  ) {
    player(
      accountId: $accountId
      name: $name
    ) {
      ${player}
    }
  }
`;

export const getProfileMatchlist = gql`
  query player($accountId: String, $id: ID, $name: String, $isRanked: Boolean, $isZeroBuild: Boolean, $offset: Int, $seasonId: ID, $mode: [GameMode!], $first: Int) {
    player(accountId: $accountId, id: $id, name: $name) {
      playerMatches(isRanked: $isRanked, isZeroBuild: $isZeroBuild, offset: $offset, seasonId: $seasonId, mode: $mode, first: $first) {
        ${playerMatch}
      }
    }
  }
`;

export const getCurrentSeason = gql`
  query currentSeason {
    currentSeason {
      ${fortniteSeasonModel}
    }
  }
`;

export const createPlayer = gql`
  mutation createPlayer($player: CreatePlayerInput!) {
    createPlayer(player: $player) {
      ${player}
    }
  }  
`;

export const createMatch = gql`
  mutation createMatch($match: CreateMatchInput!) {
    createMatch(match: $match)
  }
`;

export const getMatch = gql`
  query match($gameId: String, $id: ID, $seasonId: ID) {
    match(gameId: $gameId, id: $id, seasonId: $seasonId) {
      ${Match}
    }
  }
`;

export const updatePlayer = gql`
  mutation updatePlayer($player: UpdatePlayerInput!) {
    updatePlayer(player: $player) {
      ${player}
    }
  }
`;

export const getSeasons = gql`
  query seasons {
    seasons {
      ${fortniteSeasonModel}
    }
  }
`;

export const getPlayerSummaries = gql`
  query Summaries(
    $accountId: String!
    $isRanked: Boolean
    $isZeroBuild: Boolean
    $seasonId: ID
    $mode: GameMode
  ) {
    playerSummary(
      accountId: $accountId
      isRanked: $isRanked
      isZeroBuild: $isZeroBuild
      seasonId: $seasonId
      mode: $mode
    ) {
      wins
      matches
      kills
      downs
      timesInTopTen
      topTenPlacements {
        gameId
      }
      weaponStats {
        weapon {
          key
          name
          rarity
        }
        headshots
        totalShots
        kills
        damageDoneToPlayers
        criticalDamageDoneToPlayers
      }
    }
  }
`;

export const getPlayerWeaponStats = gql`
  query PlayerWeaponStats(
    $accountId: String!
    $seasonId: ID!
    $isRanked: Boolean
    $isZeroBuild: Boolean
    $mode: GameMode
  ) {
    playerWeaponStats(
      accountId: $accountId
      isRanked: $isRanked
      isZeroBuild: $isZeroBuild
      seasonId: $seasonId
      mode: $mode
    ) {
      kills
      headshots
      hits
      totalShots
      criticalDamageDoneToPlayers
      damageDoneToPlayers
      damageDoneToStructures
      weapon {
        key
        name
        rarity
        id
      }
    }
  }
`;

export const getPlayerRanks = gql`
  query PlayerRanks(
    $accountId: String
    $first: Int
    $offset: Int
    $seasonId: ID
  ) {
    player(accountId: $accountId) {
      ranks(first: $first, offset: $offset, seasonId: $seasonId) {
        rank
        promotionProgress
        track {
          season {
            name
            id
            endAt
          }
          rankingType
        }
      }
    }
  }
`;

export const weaponStatistics = gql`
  query WeaponStatistics(
    $seasonId: String
    $mode: String
    $playlist: String
    $rank: String
  ) {
    executeDatabricksQuery(
      game: FORTNITE
      queryName: "prod_weapon_stats"
      params: [
        { name: "season_id", value: $seasonId }
        { name: "mode", value: $mode }
        { name: "playlist", value: $playlist }
        { name: "rank", value: $rank }
      ]
    ) {
      payload
    }
  }
`;

export const rankStatistics = gql`
  query RankStatistics($seasonId: String, $playlist: String, $mode: String) {
    executeDatabricksQuery(
      game: FORTNITE
      queryName: "prod_rank_stats"
      params: [
        { name: "season_id", value: $seasonId }
        { name: "playlist", value: $playlist }
        { name: "mode", value: $mode }
      ]
    ) {
      payload
    }
  }
`;

export const weaponHistoricStats = gql`
  query WeaponHistoricStats(
    $weaponId: String
    $seasonId: String
    $mode: String
    $playlist: String
    $rank: String
  ) {
    executeDatabricksQuery(
      game: FORTNITE
      queryName: "prod_weapon_stat_window_temp"
      params: [
        { name: "weapon_id", value: $weaponId }
        { name: "season_id", value: $seasonId }
        { name: "mode", value: $mode }
        { name: "playlist", value: $playlist }
        { name: "rank", value: $rank }
      ]
    ) {
      payload
    }
  }
`;

export const getReactions = gql`
  query GetReactions($ids: [String!]!, $userId: ID) {
    allShopItemReactions(shopItemIds: $ids, userId: $userId) {
      reactionCount
      reactionType
      shopItemId
    }
  }
`;

export const addReaction = gql`
  mutation AddReaction($reactionType: String!, $id: String!, $userId: ID!) {
    shopItemReact(
      actionType: REACT
      reactionType: $reactionType
      shopItemId: $id
      userId: $userId
    ) {
      reactionCount
      reactionType
    }
  }
`;

export const removeReaction = gql`
  mutation RemoveReaction($reactionType: String!, $id: String!, $userId: ID!) {
    shopItemReact(
      actionType: REMOVE
      reactionType: $reactionType
      shopItemId: $id
      userId: $userId
    ) {
      reactionCount
      reactionType
    }
  }
`;

export const getLockerItems = gql`
  query GetLocker($fortniteAccountId: String) {
    allUserLockerItems(fortniteAccountId: $fortniteAccountId)
  }
`;

export const addToLocker = gql`
  mutation addToLocker($shopItemId: String!, $fortniteAccountId: String!) {
    addUserLockerItem(
      shopItemId: $shopItemId
      fortniteAccountId: $fortniteAccountId
    ) {
      shopItemId
    }
  }
`;

export const updateItemInLocker = gql`
  mutation updateItemInLocker(
    $shopItemId: String!
    $fortniteAccountId: String!
  ) {
    updateUserLockerItem(
      shopItemId: $shopItemId
      fortniteAccountId: $fortniteAccountId
    ) {
      shopItemId
    }
  }
`;

export const searchConsolePlayer = gql`
  query SearchUser($identifier: String!) {
    searchUser(identifier: $identifier) {
      accountId
    }
  }
`;

export const getConsoleMatches = gql`
  query MatchSummaries($accountId: String!, $inputType: String, $seasonId: ID) {
    matchSummaries(
      accountId: $accountId
      inputType: $inputType
      seasonId: $seasonId
    ) {
      ${ConsoleMatch}
    }
  }
`;
